import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
    Container,
    Box,
    Grid,
    Typography,
    CardContent,
    CardActions,
    Button,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Fab,
    Snackbar,
    Alert,
    List,
    ListItem,
    ListItemText,
    Divider,
    Chip,
    useTheme,
    useMediaQuery,
    Paper,
    Fade,
    InputAdornment
} from '@mui/material';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import { useNavigate } from 'react-router-dom';
import { getAuth } from "firebase/auth";
import SchoolIcon from '@mui/icons-material/School';
import EventNoteIcon from '@mui/icons-material/EventNote';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import ClassIcon from '@mui/icons-material/Class';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SearchIcon from '@mui/icons-material/Search';
import {useLanguage} from "./LanguageContext";

export default function MyClasses() {
    const [loading, setLoading] = useState(true);
    const [classes, setClasses] = useState([]);
    const [events, setEvents] = useState([]);
    const [joinDialogOpen, setJoinDialogOpen] = useState(false);
    const [joinCode, setJoinCode] = useState('');
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });
    const [searchTerm, setSearchTerm] = useState('');
    const navigate = useNavigate();
    const [selectedDate, setSelectedDate] = useState(new Date());
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { t } = useLanguage();

    useEffect(() => {
        document.title = `${t.myClasses} | ClassCapture`;
    }, [t.myClasses]);

    useEffect(() => {
        const fetchClasses = async () => {
            try {
                const auth = getAuth();
                const token = await auth.currentUser.getIdToken();
                const url = `https://classroomservices-470732867211.us-central1.run.app/api/classroom/`;
                const response = await axios.get(url, {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                });
                setClasses(response.data.classes);
                setLoading(false);
            } catch (error) {
                console.error('Error fetching classes', error);
                setLoading(false);
            }
        };

        fetchClasses();
    }, []);

    useEffect(() => {
        fetchUserEvents();
    }, []);

    const fetchUserEvents = async () => {
        try {
            const auth = getAuth();
            const token = await auth.currentUser.getIdToken();
            const response = await axios.get('https://classroomservices-470732867211.us-central1.run.app/api/classroom/events', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setEvents(response.data.events);
        } catch (error) {
            console.error('Error fetching events', error);
        }
    };

    const handleDetailsClick = (classroom) => {
        navigate(`/class/${classroom.cid}`, { state: { classroom: classroom } });
    };

    const handleOpenJoinDialog = () => {
        setJoinDialogOpen(true);
    };

    const handleCloseJoinDialog = () => {
        setJoinDialogOpen(false);
        setJoinCode('');
    };

    const handleJoinClass = async () => {
        try {
            const auth = getAuth();
            const token = await auth.currentUser.getIdToken();
            const url = `https://classroomservices-470732867211.us-central1.run.app/api/classroom/join`;
            const response = await axios.post(url, { joinCode: joinCode }, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });
            setSnackbar({ open: true, message: response.data.error || "Joined class successfully!", severity: "success" });
            handleCloseJoinDialog();
            const classesResponse = await axios.get(`https://classroomservices-470732867211.us-central1.run.app/api/classroom/`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setClasses(classesResponse.data.classes);
        } catch (error) {
            console.error("Error joining class:", error);
            setSnackbar({ open: true, message: error.response?.data.error || "Error joining class", severity: "error" });
        }
    };

    const handleSnackbarClose = (event, reason) => {
        if (reason === 'clickaway') return;
        setSnackbar({ ...snackbar, open: false });
    };

    const onDateChange = (date) => {
        setSelectedDate(date);
    };

    // Fonction pour obtenir les événements d'une date spécifique
    const getEventsForDate = (date) => {
        return events.filter(event => {
            const eventDate = new Date(event.eventId);
            return (
                eventDate.getDate() === date.getDate() &&
                eventDate.getMonth() === date.getMonth() &&
                eventDate.getFullYear() === date.getFullYear()
            );
        });
    };

    // Style personnalisé pour les cases du calendrier
    const tileClassName = ({ date, view }) => {
        if (view === 'month') {
            const dateEvents = getEventsForDate(date);
            if (dateEvents.length > 0) {
                return 'has-events';
            }
        }
        return null;
    };

    // Contenu personnalisé pour les cases du calendrier
    const tileContent = ({ date, view }) => {
        if (view !== 'month') return null;
        const dateEvents = getEventsForDate(date);
        if (dateEvents.length === 0) return null;

        // Détection des types présents ce jour-là
        let hasCourse = false;
        let hasExam   = false;
        dateEvents.forEach(evt => {
            if (Array.isArray(evt.events)) {
                evt.events.forEach(item => {
                    const type = typeof item === 'object' ? item.type : null;
                    if (type === 'exam') hasExam = true;
                    else               hasCourse = true;
                });
            }
        });

        // Style de base
        const base = {
            position: 'absolute',
            top: 2, right: 2,
            width: 6, height: 6,
            borderRadius: '50%',
        };

        // Choix de la couleur ou du dégradé
        let style;
        if (hasCourse && !hasExam) {
            style = { ...base, backgroundColor: theme.palette.primary.main };
        } else if (!hasCourse && hasExam) {
            style = { ...base, backgroundColor: theme.palette.error.main };
        } else /* both */ {
            style = {
                ...base,
                backgroundImage: `linear-gradient(45deg, 
        ${theme.palette.primary.main} 50%, 
        ${theme.palette.error.main} 50%
      )`
            };
        }

        return <Box sx={style} />;
    };

    // Filtrer les événements pour la date sélectionnée
    const eventsForSelectedDate = getEventsForDate(selectedDate);

    // Filtrer les classes en fonction de la recherche
    const filteredClasses = classes.filter(cl =>
        cl.metadata.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (cl.metadata.description && cl.metadata.description.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    if (loading) {
        return (
            <Container sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                flexDirection: 'column',
                gap: 1
            }}>
                <SchoolIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />
                <Typography variant="subtitle1" component="h1" color="primary" fontWeight={450}>
                    ClassCapture
                </Typography>
                <CircularProgress color="primary" size={24} />
            </Container>
        );
    }

    const flatEvents = eventsForSelectedDate.flatMap(evtObj =>
        evtObj.events.map((e, subIdx) => ({
            title: typeof e === 'string' ? e : e.title || t.noTitle,
            description: typeof e === 'string' ? e : e.description || t.noDescription,
            type: typeof e === 'object' ? e.type : null,
            className: evtObj.className,
            date: new Date(evtObj.eventId),
            key: `${evtObj.eventId}-${subIdx}`
        }))
    );

    const gradientBackground = {
        background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
    };

    if (loading) {
        return (
            <Container sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                flexDirection: 'column',
                gap: 1
            }}>
                <SchoolIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />
                <Typography variant="subtitle1" component="h1" color="primary" fontWeight={450}>
                    ClassCapture
                </Typography>
                <CircularProgress color="primary" size={24} />
            </Container>
        );
    }

    return (
        <Container component="main" maxWidth="lg" sx={{ py: 2 }}>
            {/* Header - Version compacte */}
            <Fade in={true} timeout={800}>
                <Paper
                    elevation={1}
                    sx={{
                        p: 2,
                        mb: 2,
                        borderRadius: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        ...gradientBackground,
                        color: 'white'
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <SchoolIcon sx={{ fontSize: 24, mr: 1, color:"black" }} />
                        <Typography variant="h6" component="h1" fontWeight={450} color={"black"}>
                            {t.myClasses}
                        </Typography>
                    </Box>

                    <TextField
                        placeholder={t.searchPlaceholder}
                        variant="outlined"
                        size="small"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        sx={{
                            width: isMobile ? '100px' : '180px',
                            backgroundColor: 'rgba(255, 255, 255, 0.15)',
                            borderRadius: 1,
                            '& .MuiOutlinedInput-root': {
                                '& fieldset': {
                                    borderColor: 'rgba(255, 255, 255, 0.5)',
                                },
                                '&:hover fieldset': {
                                    borderColor: 'rgba(255, 255, 255, 0.3)',
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: 'white',
                                },
                            },
                            '& input': {
                                color: 'white',
                                padding: '6px 10px',
                                fontSize: '0.875rem',
                            }
                        }}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <SearchIcon sx={{ color: 'white', fontSize: 18 }} />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Paper>
            </Fade>

            {/* Section Calendrier et Événements */}
            <Fade in={true} timeout={1000}>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                    {/* Calendrier */}
                    <Grid item xs={12} md={6}>
                        <Paper
                            elevation={1}
                            sx={{
                                p: 2,
                                borderRadius: 1,
                                backgroundColor: 'background.paper',
                                height: '100%',
                                overflow: 'hidden'
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <EventNoteIcon sx={{ fontSize: 16, mr: 1, color: theme.palette.primary.main }} />
                                <Typography variant="subtitle1" fontWeight={450}>
                                    {t.calendar}
                                </Typography>
                            </Box>

                            <Calendar
                                className="compact-calendar"
                                tileClassName={tileClassName}
                                tileContent={tileContent}
                                onChange={onDateChange}
                                value={selectedDate}
                            />

                            <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'center' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'primary.main' }} />
                                    <Typography variant="caption">{t.courses}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'error.main' }} />
                                    <Typography variant="caption">{t.exam}</Typography>
                                </Box>
                            </Box>
                        </Paper>
                    </Grid>

                    {/* Événements pour la date sélectionnée */}
                    <Grid item xs={12} md={6}>
                        <Paper
                            elevation={1}
                            sx={{
                                p: 2,
                                borderRadius: 1,
                                backgroundColor: 'background.paper',
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column'
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <EventNoteIcon sx={{ fontSize: 16, mr: 1, color: theme.palette.secondary.main }} />
                                <Typography variant="subtitle1" fontWeight={450}>
                                    {t.eventsFor} {new Date(selectedDate).toLocaleDateString()}
                                </Typography>
                            </Box>

                            <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
                                {flatEvents.length > 0 ? (
                                    <List dense disablePadding>
                                        {flatEvents.map((evt, idx) => (
                                            <React.Fragment key={evt.key}>
                                                <ListItem
                                                    sx={{
                                                        bgcolor: theme.palette.background.default,
                                                        borderRadius: 1,
                                                        mb: 0.5,
                                                        py: 0.75
                                                    }}
                                                >
                                                    <ListItemText
                                                        primary={
                                                            <Typography variant="body2" fontWeight={450}>
                                                                {evt.title}
                                                            </Typography>
                                                        }
                                                        secondary={
                                                            <Typography component="span" variant="caption" color="text.secondary">
                                                                {evt.description}
                                                            </Typography>
                                                        }
                                                    />
                                                    <Chip
                                                        label={evt.className}
                                                        color={
                                                            evt.type === 'exam' ? 'error' :
                                                                evt.type === 'assignment' ? 'warning' :
                                                                    'primary'
                                                        }
                                                        size="small"
                                                        sx={{ ml: 1, height: 20, '& .MuiChip-label': { px: 1, fontSize: '0.7rem' } }}
                                                    />
                                                </ListItem>
                                                {idx < flatEvents.length - 1 && <Divider sx={{ my: 0.5 }} />}
                                            </React.Fragment>
                                        ))}
                                    </List>
                                ) : (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            height: '100%',
                                            py: 2
                                        }}
                                    >
                                        <InfoOutlinedIcon sx={{ fontSize: 24, color: 'text.secondary', mb: 1 }} />
                                        <Typography variant="caption" color="text.secondary" align="center">
                                            {t.noEvents}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>
            </Fade>

            {/* Section Classes */}
            <Box sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                <Typography variant="subtitle1" component="h2" fontWeight={450}>
                    {t.yourClasses}
                </Typography>
                <Divider sx={{ ml: 2, flexGrow: 1 }} />
            </Box>

            {/* Liste des classes */}
            {filteredClasses.length > 0 ? (
                <Fade in={true} timeout={1200}>
                    <Grid container spacing={2}>
                        {filteredClasses.map(cl => (
                            <Grid item xs={12} sm={6} md={4} key={cl.cid || cl.id}>
                                <Paper
                                    elevation={1}
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        height: '100%',
                                        borderRadius: 1,
                                        overflow: 'hidden',
                                        transition: 'transform 0.15s, box-shadow 0.15s',
                                        '&:hover': {
                                            transform: 'translateY(-2px)',
                                            boxShadow: 2,
                                        }
                                    }}
                                >
                                    <Box
                                        sx={{
                                            p: 1,
                                            ...gradientBackground,
                                            color: 'black',
                                            display: 'flex',
                                            alignItems: 'center'
                                        }}
                                    >
                                        <ClassIcon sx={{ mr: 1, fontSize: 16 }} />
                                        <Typography variant="body2" fontWeight={450} noWrap>
                                            {cl.metadata.title}
                                        </Typography>
                                    </Box>

                                    <CardContent sx={{ flexGrow: 1, p: 1.5 }}>
                                        <Typography variant="caption" color="text.secondary" sx={{
                                            mb: 1,
                                            display: '-webkit-box',
                                            WebkitLineClamp: 2,
                                            WebkitBoxOrient: 'vertical',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            lineHeight: 1.3
                                        }}>
                                            {cl.metadata.description || t.noDescription}
                                        </Typography>

                                        <Box sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            mt: 'auto'
                                        }}>
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{ fontSize: '0.65rem' }}
                                            >
                                                {cl.timestamps?.createdAt ?
                                                    new Date(cl.timestamps.createdAt).toLocaleDateString() :
                                                    t.noDate}
                                            </Typography>
                                        </Box>
                                    </CardContent>

                                    <Divider />

                                    <CardActions sx={{ p: 0 }}>
                                        <Button
                                            fullWidth
                                            onClick={() => handleDetailsClick(cl)}
                                            endIcon={<ArrowForwardIcon sx={{ fontSize: 16 }} />}
                                            sx={{
                                                p: 0.75,
                                                textTransform: 'none',
                                                fontWeight: 500,
                                                justifyContent: 'space-between',
                                                fontSize: '0.75rem'
                                            }}
                                        >
                                            {t.viewDetails}
                                        </Button>
                                    </CardActions>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                </Fade>
            ) : (
                <Fade in={true} timeout={1200}>
                    <Paper
                        elevation={1}
                        sx={{
                            p: 2,
                            textAlign: 'center',
                            borderRadius: 1,
                            bgcolor: 'background.paper'
                        }}
                    >
                        {searchTerm ? (
                            <>
                                <InfoOutlinedIcon sx={{ fontSize: 24, color: 'text.secondary', mb: 1 }} />
                                <Typography variant="body2" gutterBottom>
                                    {t.noMatchingClasses}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {t.noMatchingClassesDesc}
                                </Typography>
                            </>
                        ) : (
                            <>
                                <SchoolIcon sx={{ fontSize: 24, color: 'text.secondary', mb: 1 }} />
                                <Typography variant="body2" gutterBottom>
                                    {t.noClassesAvailable}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" gutterBottom>
                                    {t.noClassesAvailableDesc}
                                </Typography>
                            </>
                        )}
                    </Paper>
                </Fade>
            )}

            {/* Bouton FAB fixé en bas à droite */}
            <Fab
                color="primary"
                aria-label="join"
                onClick={handleOpenJoinDialog}
                size="small"
                sx={{
                    position: 'fixed',
                    bottom: 16,
                    right: 16,
                    boxShadow: 2
                }}
            >
                <GroupAddIcon sx={{ fontSize: 18 }} />
            </Fab>

            {/* Dialogue pour entrer le code de la classe */}
            <Dialog
                open={joinDialogOpen}
                onClose={handleCloseJoinDialog}
                fullWidth
                maxWidth="xs"
                PaperProps={{
                    sx: {
                        borderRadius: 1,
                        overflow: 'hidden'
                    }
                }}
            >
                <Box sx={{
                    ...gradientBackground,
                    color: 'white',
                    p: 1.5,
                    display: 'flex',
                    alignItems: 'center'
                }}>
                    <GroupAddIcon sx={{ mr: 1, fontSize: 18 }} />
                    <DialogTitle sx={{ p: 0, fontSize: '1rem' }}>{t.joinClassTitle}</DialogTitle>
                </Box>
                <DialogContent sx={{ mt: 1, pt: 1 }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        {t.joinCodePrompt}
                    </Typography>
                    <TextField
                        autoFocus
                        label={t.joinCodeLabel}
                        fullWidth
                        variant="outlined"
                        size="small"
                        value={joinCode}
                        onChange={(e) => setJoinCode(e.target.value)}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 2, pb: 2 }}>
                    <Button
                        onClick={handleCloseJoinDialog}
                        size="small"
                        sx={{ textTransform: 'none' }}
                    >
                        {t.cancel}
                    </Button>
                    <Button
                        variant="contained"
                        size="small"
                        onClick={handleJoinClass}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 500,
                            px: 2
                        }}
                    >
                        {t.joinClass}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar pour les notifications */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    onClose={handleSnackbarClose}
                    severity={snackbar.severity}
                    sx={{ width: '100%', py: 0.5 }}
                    elevation={2}
                    variant="filled"
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>

            {/* Styles CSS pour le calendrier compact */}
            <style>
                {`
                .has-events {
                    position: relative;
                }
                
                .compact-calendar {
                    width: 100%;
                    border: none;
                    border-radius: 4px;
                    padding: 4px;
                    font-family: inherit;
                    font-size: 0.8rem;
                }
                
                .react-calendar button {
                    border-radius: 2px;
                }
                
                .react-calendar__tile {
                    position: relative;
                    overflow: visible;
                    padding: 8px 4px;
                    font-size: 0.75rem;
                    font-weight: 500;
                }
                
                .react-calendar__tile--now {
                    background-color: ${theme.palette.secondary.light}40;
                }
                
                .react-calendar__tile--active {
                    background-color: ${theme.palette.primary.main};
                    color: white;
                }
                
                .react-calendar__tile--active.has-events {
                    background-color: ${theme.palette.primary.main};
                    color: white;
                }
                
                .react-calendar__navigation {
                    height: 30px;
                }
                
                .react-calendar__navigation button {
                    border-radius: 2px;
                    font-weight: 500;
                    font-size: 0.8rem;
                    min-width: auto;
                }
                
                .react-calendar__navigation button:enabled:hover,
                .react-calendar__navigation button:enabled:focus {
                    background-color: ${theme.palette.primary.light}30;
                }
                
                .react-calendar__tile:enabled:hover,
                .react-calendar__tile:enabled:focus {
                    background-color: ${theme.palette.primary.light}30;
                }
                
                .react-calendar__month-view__weekdays {
                    font-size: 0.7rem;
                }
                
                .react-calendar__viewContainer {
                    padding: 0;
                }
                `}
            </style>
        </Container>
    );
}